import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { ENTITY_LOG_KEY } from '@/shared/hooks/use-workflows'
import type {
  GroupAddress,
  GroupContact,
  GroupContactInput,
  GroupPropertyCreateInput,
} from '@/admin/hooks/use-property-groups'

/** The property record: /api/registrations/properties/<uuid>/. */
export interface PropertyRecord {
  uuid: string
  address: GroupAddress
  address_display: string
  owner: { first_name: string; last_name: string; email: string; phone: string }
  mailing_address: Omit<GroupAddress, 'country'> | null
  co_owners: GroupContact[]
  group: { uuid: string; name: string } | null
  host_attribute_values: Record<string, unknown>
  structures: { uuid: string; label: string; values: Record<string, unknown> }[]
  registrations: {
    uuid: string
    registration_number: string
    product: string
    structure: string
    warranty_class: string
    status: string
    status_label: string
    is_active: boolean
    registered_at: string | null
  }[]
  claims: {
    uuid: string
    claim_number: string
    registration_number: string
    status_label: string
    is_open: boolean
    created_at: string | null
  }[]
  transfers: {
    uuid: string
    reference: string
    status: string
    status_label: string
    is_open: boolean
    updated_at: string | null
  }[]
}

const BASE = '/registrations/properties/'
const unwrap = <T,>(r: { data: { data?: T } & T }): T =>
  (r.data?.data ?? r.data) as T

export const propertyKey = (uuid: string) => ['property', uuid] as const

export function useProperty(uuid: string | undefined) {
  return useQuery<PropertyRecord>({
    queryKey: propertyKey(uuid ?? ''),
    queryFn: () => api.get(`${BASE}${uuid}/`).then(unwrap<PropertyRecord>),
    enabled: !!uuid,
  })
}

function useRefresh(uuid: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: propertyKey(uuid) })
    void qc.invalidateQueries({ queryKey: ENTITY_LOG_KEY })
    void qc.invalidateQueries({ queryKey: ['property-groups'] })
  }
}

export function useAddCoOwner(uuid: string) {
  const refresh = useRefresh(uuid)
  return useMutation<PropertyRecord, Error, GroupContactInput>({
    mutationFn: (data) =>
      api.post(`${BASE}${uuid}/co-owners/`, data).then(unwrap<PropertyRecord>),
    onSuccess: refresh,
  })
}

export function useDeactivateRegistration(uuid: string) {
  const refresh = useRefresh(uuid)
  return useMutation<PropertyRecord, Error, { holdingUuid: string; reason: string }>({
    mutationFn: ({ holdingUuid, reason }) =>
      api
        .post(`${BASE}${uuid}/registrations/${holdingUuid}/deactivate/`, { reason })
        .then(unwrap<PropertyRecord>),
    onSuccess: refresh,
  })
}

export function useCreateProperty() {
  const qc = useQueryClient()
  return useMutation<
    { uuid: string; address: string },
    Error,
    GroupPropertyCreateInput & { group_uuid?: string | null }
  >({
    mutationFn: (data) =>
      api.post(BASE, data).then(unwrap<{ uuid: string; address: string }>),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['property-groups'] }),
  })
}

export interface PropertyOptions {
  property_type_options: { value: string; label: string }[]
  /** The property type a property in an HOA starts with. */
  default_property_type?: string
  groups: { uuid: string; name: string }[]
}

export function usePropertyOptions() {
  return useQuery<PropertyOptions>({
    queryKey: ['property-options'],
    queryFn: () => api.get(`${BASE}options/`).then(unwrap<PropertyOptions>),
  })
}
